import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { isRTL } from "../../../i18n/config";
import CrossIcon from "../../../images/cross.svg"
import {
  PutColumnPreference,
  PostColumnPreference,
} from "../../../api/column_pref";
import "./customize_column.scss";
import CutomColumn from "../../../images/customcolumn.svg";
import CustomModal from "../CustomModal/CustomModal";
import CrossButton from "../ButtonSpinner/CrossButton";
import GeneralButton from "../SaveButton/GeneralButton";
import { Col, Row } from "react-bootstrap";

export const GetColmn = async (setCheckedStateDict, page_url, checkedStateDict, rowsPerPage, setRowsPerPage) => {
  if (checkedStateDict) {
    const { data: postData, error } = await PostColumnPreference(checkedStateDict, page_url, rowsPerPage);
    if (error) {
      setCheckedStateDict(checkedStateDict);
      if (setRowsPerPage) setRowsPerPage(rowsPerPage || 10);
      return;
    }
    if (postData?.data?.options && Object.keys(postData.data.options).length > 0) {
      const returnedOptions = postData.data.options;
      const currentKeys = Object.keys(checkedStateDict);
      const keysMatch = currentKeys.length === Object.keys(returnedOptions).length &&
                        currentKeys.every(k => k in returnedOptions);
      setCheckedStateDict(keysMatch ? returnedOptions : checkedStateDict);
    }
    if (setRowsPerPage) {
      setRowsPerPage(postData?.data?.rows_per_page || rowsPerPage || 10);
    }
  }
};

/**
 * Load column preference from backend and then call setPreferenceLoaded(true).
 * Use in a useEffect on mount so pages can gate their data fetch on preferenceLoaded.
 */
export const loadColumnPreference = async (
  setCheckedStateDict,
  page_url,
  checkedStateDict,
  rowsPerPage,
  setRowsPerPage,
  setPreferenceLoaded
) => {
  try {
    await GetColmn(setCheckedStateDict, page_url, checkedStateDict, rowsPerPage, setRowsPerPage);
  } catch (error) {
    console.error("Error loading column preference:", error);
  } finally {
    if (setPreferenceLoaded) setPreferenceLoaded(true);
  }
};

export const CustomizeColumn = ({
  page_url,
  setCheckedStateDict,
  checkedStateDict,
  pageType = null,
  customizeColumnDict = null,
  rowsPerPage,
  setRowsPerPage,
  columnOrder = null,
}) => {
  const { t, i18n } = useTranslation();
  const isRtlMode = isRTL(i18n.language);
  const [show, setShow] = useState(false);
  const [tempCheckedStateDict, setTempCheckedStateDict] = useState({});

  const handleShow = () => {
    if (customizeColumnDict) {
      const normalized = {};
      Object.keys(customizeColumnDict).forEach(key => {
        normalized[key] = checkedStateDict[key] ?? customizeColumnDict[key];
      });
      setTempCheckedStateDict(normalized);
    } else {
      setTempCheckedStateDict({ ...checkedStateDict });
    }
    setShow(true);
    document.addEventListener("keydown", handleKeyDown);
  };

  const handleClose = () => {
    setTempCheckedStateDict({}); 
    setShow(false);
    document.removeEventListener("keydown", handleKeyDown);
  };

  const handleSave = async () => {
    const { data, error } = await PutColumnPreference(tempCheckedStateDict, page_url, rowsPerPage);
    if (data) {
      setCheckedStateDict(data.data.options); 
    }
    setTempCheckedStateDict({}); 
    handleClose();
  };

  const handleKeyDown = (event) => {
    if (event.key === "Escape") {
      handleClose();
    }
  };

  const handleOnChangeDict = (item) => {
    const updatedCheckedState = { ...tempCheckedStateDict };
    updatedCheckedState[item] = !updatedCheckedState[item];
    setTempCheckedStateDict(updatedCheckedState);
  };

  const keysToDisplay = useMemo(() => {
    if (customizeColumnDict) {
      return Object.keys(customizeColumnDict);
    }
    return Object.keys(tempCheckedStateDict);
  }, [customizeColumnDict, tempCheckedStateDict]);

  const options = keysToDisplay
    .filter(item => {
      if (columnOrder && !columnOrder.includes(item)) return false;
      if (!pageType) return true; 
      

      if (pageType.includes("allocation")) {
        const isUserId = item === "reports.userId";
        const isGroupId = item === "reports.groupId";
        const isRoleId = item === "reports.roleID";

        if (isUserId && !pageType.includes("allocation_user")) return false;
        if (isGroupId && !pageType.includes("allocation_group")) return false;
        if (isRoleId && !pageType.includes("allocation_role")) return false;
      }
      
      return true;
    })
    .sort((a, b) => {
      if (!columnOrder) return 0;
      const indexA = columnOrder.indexOf(a);
      const indexB = columnOrder.indexOf(b);
      if (indexA === -1 && indexB === -1) return 0;
      if (indexA === -1) return 1;
      if (indexB === -1) return -1;
      return indexA - indexB;
    })
    .map((item) => {
      const actualKey = item;
      
      return (
        <div
          key={actualKey}
          className="mb-2 rounded custom-pointer border p-2"
          onClick={() => handleOnChangeDict(actualKey)}
        >
          <input
            type="checkbox"
            className={`${isRtlMode ? "ms-3" : "me-3"} custom-pointer checkbox_size`}
            checked={tempCheckedStateDict[actualKey] ?? (customizeColumnDict?.[actualKey] ?? false)}
            onChange={() => handleOnChangeDict(actualKey)}
          />
          <label className="custom-pointer">{t(item)}</label>
        </div>
      );
    });

  return (
    <>
      <CustomModal open={show} handleClose={handleClose} width="30% !important">
        <div>
          <div className="d-flex justify-content-between align-items-center  mb-4">
            <h3 className={`border-0 customize-column-name mb-0 ${isRtlMode ? "ms-5" : "me-5"}`}>{t("common.customizeColumns")}</h3>
            <CrossButton onClick={() =>{
              setShow(false)
            }}></CrossButton>
          </div>
          <div className="custom-modal-body pb-0">
            {options}
          </div>
          <Row className="pt-4 mb-3 justify-content-end">
            <Col sm="auto">
              <GeneralButton
                onClickEvent={handleClose}
                className={isRtlMode ? "ms-1" : "me-1"}
                value={t("common.cancel")}
                color="#505050"
                variant="outlined"
                size="medium"
              />
            </Col>
            <Col sm="auto" className={`${isRtlMode ? "ms-sm-2" : "me-sm-2"} p-0`}>
              <GeneralButton
                onClickEvent={handleSave}
                className={isRtlMode ? "ms-1" : "me-1"}
                value={t("common.save")}
                variant="contained"
                size="medium"
              />
            </Col>
          </Row>
        </div>
      </CustomModal>
      <div className="d-flex align-items-center qu">
        <button onClick={handleShow} className="advance_filter_button">
          <img src={CutomColumn} alt="icon" id="center-blueShadow" />
        </button>
      </div>
    </>
  );
};
        